// ============================================================================
// RECEIPT WORKSHEET SERVICE — BU-level Receipt Verification Worksheet
// (ADR-0024 Leg 2). Inbox is scope-filtered by X-Entity-ID / X-Business-Unit-ID.
// Action mapping: UPDATE = save draft, APPROVE = post, REJECT = return.
// ============================================================================

import { get, post, put } from '../client/axiosClient';
import { receiptWorksheetEndpoints } from '../endpoints/banking.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  ID,
  ReceiptAssignmentDto,
  ReceiptAssignmentQueryParams,
  SaveAllocationRequest,
  PostAllocationRequest,
  ReturnAssignmentRequest,
  OutstandingOpenItemDto,
} from '../types';

class ReceiptWorksheetService {
  /**
   * Paged worksheet inbox. The backend defaults assignmentStatus to PENDING when
   * the parameter is ABSENT; pass a single space (' ') to request all statuses —
   * the repository treats whitespace as "no status filter" while the query
   * builder drops empty strings entirely (which would re-trigger the default).
   */
  async getAll(params?: ReceiptAssignmentQueryParams): Promise<PaginatedResponse<ReceiptAssignmentDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(receiptWorksheetEndpoints.list(), params)
      : receiptWorksheetEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<ReceiptAssignmentDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  /** Full worksheet item including draft/posted allocation lines. */
  async getById(id: ID): Promise<ReceiptAssignmentDto> {
    const response = await get<ApiResponse<ReceiptAssignmentDto>>(receiptWorksheetEndpoints.getById(id));
    return response.data.data;
  }

  /**
   * Outstanding DR open items for a customer (the AGAINST_REF picker). The
   * backend resolves the party's AR ledger via accounts.tbl_party_ledger —
   * empty array when the party has no receivables ledger / no open bills.
   * Pass the worksheet item's id to restrict the bills to that assignment's
   * branch (the allocation view always does).
   */
  async getOutstandingItems(partyId: ID, assignmentId?: ID): Promise<OutstandingOpenItemDto[]> {
    const url = buildEndpointWithQuery(receiptWorksheetEndpoints.outstandingItems(), { partyId, assignmentId });
    const response = await get<ApiResponse<OutstandingOpenItemDto[]>>(url);
    return response.data.data ?? [];
  }

  /** Why an invoice can be short-paid (ADR-0060) — the Reason picker. */
  async getDeductionReasons(): Promise<{ value: string; label: string }[]> {
    const response = await get<ApiResponse<{ value: string; label: string }[]>>(receiptWorksheetEndpoints.deductionReasons());
    return response.data.data ?? [];
  }

  /** Save the draft allocation lines without posting (UPDATE action). */
  async saveAllocation(id: ID, data: SaveAllocationRequest): Promise<ReceiptAssignmentDto> {
    const response = await put<ApiResponse<ReceiptAssignmentDto>>(receiptWorksheetEndpoints.saveAllocation(id), data);
    return response.data.data;
  }

  /** Post the allocation — Leg 2 voucher (APPROVE action). */
  async postAllocation(id: ID, data: PostAllocationRequest): Promise<ReceiptAssignmentDto> {
    const response = await post<ApiResponse<ReceiptAssignmentDto>>(receiptWorksheetEndpoints.post(id), data);
    return response.data.data;
  }

  /** Return the item to the capturer with a reason (REJECT action). */
  async return(id: ID, data: ReturnAssignmentRequest): Promise<ReceiptAssignmentDto> {
    const response = await post<ApiResponse<ReceiptAssignmentDto>>(receiptWorksheetEndpoints.return(id), data);
    return response.data.data;
  }
}

export const receiptWorksheetService = new ReceiptWorksheetService();
export default receiptWorksheetService;
